// src/features/project/ItemKeyLookup.tsx
// Jump to an item by its human-readable key (e.g. MP-123). Tries the project-scoped
// resolver first (GetItemByKey), then falls back to the global one (ResolveItemKey)
// so a key from another project still resolves.

import { useState } from 'react';
import { useI18n, useProjectItem } from '@react/hooks';
import { Input } from '@react/components';
import type { Entity } from '@core/types/entity';
import type { ProjectItem } from '@core/types/project';

interface ItemKeyLookupProps {
  projectEntity: Entity;
  onfound: (item: ProjectItem) => void;
}

export function ItemKeyLookup({ projectEntity, onfound }: ItemKeyLookupProps) {
  const { t } = useI18n();
  const store = useProjectItem();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = key.trim();
    if (!value) return;
    setBusy(true);
    setNotFound(false);
    try {
      let item: ProjectItem | null = null;
      try {
        item = await store.GetItemByKey(projectEntity.id, value);
      } catch {
        // fall through to the global resolver
      }
      if (!item || !item.entity) {
        const res = await store.ResolveItemKey(value);
        item = res.item ?? null;
      }
      if (item && item.entity) {
        onfound(item);
        setKey('');
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="relative">
      <label htmlFor="item-key-lookup" className="sr-only">
        {t('project.item.keyLookup')}
      </label>
      <Input
        id="item-key-lookup"
        value={key}
        onChange={(e) => {
          setKey(e.target.value);
          setNotFound(false);
        }}
        disabled={busy}
        placeholder={t('project.item.keyLookupPlaceholder')}
        className="w-32 font-mono"
      />
      {notFound && (
        <p className="absolute left-0 top-full mt-1 text-xs text-destructive" role="status">
          {t('project.item.keyNotFound')}
        </p>
      )}
    </form>
  );
}
